import type {ChangeEventHandler, ReactElement, ReactNode} from 'react';
import {useEffect, useState} from 'react';

import {Input} from '@/components/common/Input';
import SpinningLoader from '@/components/common/SpinningLoader';
import ImageViewer from '@/components/ImageViewer';
import {fileService, rateBucketKey} from '@/services/fileService';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

type FilePickerProps = {
  name: string;
  label: string;
  onChange: (url: string) => void;
  alt?: string;
  value?: string;
  required?: boolean;
};

const ImagePicker = ({
  name,
  label,
  onChange,
  alt,
  value,
  ...props
}: FilePickerProps): ReactElement => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const handleUpload = async (e: InputEvent) => {
    try {
      setIsLoading(true);
      const blob: Blob = (e.target as HTMLInputElement)?.files?.[0] as Blob;
      const {getUrl} = await fileService.uploadFile(
        name,
        rateBucketKey,
        null,
        blob,
      );
      setUrl(getUrl);
      onChange(getUrl);
      setIsLoading(false);
    } catch (e: unknown) {
      setIsLoading(false);
      showErrorMessage(
        'Failed to upload image: ' + getErrorMessage(e, String(e)),
      );
    }
  };

  useEffect(() => {
    if (value) {
      fileService
        .fetchUploadedFile(value)
        .then((res) => {
          if (res) {
            setUrl(res);
          }
        })
        // Background preview fetch: `fetchUploadedFile` does not catch, and a
        // failure here has always just left the preview empty. Swallow rather
        // than toast so the visible behaviour is unchanged.
        .catch(() => {});
    }
  }, [value]);

  let content: ReactNode;
  if (isLoading) {
    content = <SpinningLoader />;
  } else if (url) {
    content = <ImageViewer src={url} alt={alt} />;
  } else {
    content = (
      <Input
        label={label}
        type="file"
        {...props}
        onChange={
          handleUpload as unknown as ChangeEventHandler<HTMLInputElement>
        }
      />
    );
  }

  return <div className="mt-5">{content}</div>;
};

export default ImagePicker;
